'use client'

import classNames from 'classnames'
import { useAuth } from '@/contexts/Auth'
import { useRouter } from 'next/navigation'
import { getAuthConfigByRole, AuthConfig } from '@/config/authConfig'
import { useMemo, useEffect } from 'react'
import ContentPasteSearch from '../intake/components/Icons/ContentPasteSearch'
import Rocket from '../intake/components/Icons/Rocket'
import { ScreenLoader } from '../components/molecules/ScreenLoader'

const Page = () => {
  const router = useRouter()
  const { user } = useAuth()

  // Determine auth configuration based on user email domain
  const authConfig: AuthConfig = useMemo(() => {
    const userRole = user?.email?.endsWith('@talentneuron.com')
      ? 'full-access'
      : 'intake'
    return getAuthConfigByRole(userRole)
  }, [user?.email])

  // Redirect non-@talentneuron.com users to /intake
  useEffect(() => {
    if (user?.email && !user.email.endsWith('@talentneuron.com')) {
      router.push('/intake')
    }
  }, [user?.email, router])

  // Get icon component based on icon name
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'ContentPasteSearch':
        return <ContentPasteSearch />
      case 'Rocket':
        return <Rocket />
      default:
        return <ContentPasteSearch />
    }
  }

  if (!user) return <ScreenLoader />

  return (
    <div className='flex items-center h-screen'>
      <div className='mx-auto rounded-xl p-9 border bg-light-background-1 w-full max-w-fit'>
        <div className='mb-12'>
          <div
            className={'text-[#130056] font-semibold'}
            style={{
              marginTop: '1px',
              fontSize: '27px'
            }}
          >
            Your TalentNeuron Portal
          </div>
          <p className='text-gray-600 text-xs'>
            Select one option to continue.
          </p>
        </div>

        <div
          className={`grid gap-6 ${authConfig.tabs.length === 1 ? 'grid-cols-1 mx-auto' : 'md:grid-cols-2'}`}
        >
          {authConfig.tabs.map((tab, index) => (
            <div
              key={tab.id}
              className={classNames(
                'cursor-pointer w-[590px] bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-shadow duration-200',
                index === authConfig.tabs.length - 1 && 'bg-accentGradient'
              )}
            >
              <div className='mb-6'>
                <div className='w-11 h-11 flex justify-center items-center rounded-full border-[4px] border-status-blue-gray'>
                  {getIconComponent(tab.icon)}
                </div>
                <h2 className='pt-6 text-2xl font-semibold text-gray-900 mb-2'>
                  {tab.name}
                </h2>
                <p className='text-gray-500'>{tab.description}</p>
              </div>
              <button
                className={classNames(
                  'w-full py-2.5',
                  'rounded-md',
                  'border-primary-gradient shadow-[0_2px_4px_0_rgba(0,0,0,0.3)]',
                  'text-sm text-blue-purple font-semibold'
                )}
                onClick={() => {
                  router.push(tab.route)
                }}
                disabled={!tab.enabled}
              >
                Select {tab.name}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Page;
